/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {Company} from 'types/entities/Company'
import {CompanyAddress} from 'types/entities/CompanyAddress'
import {CompanyContact} from 'types/entities/CompanyContact'
import {
    formatGenericContactLocalLabel,
    isGenericCompanyContact,
    pickPreferredCompanyContact,
} from 'utils/contacts/genericContactUtils'
import {Reservation} from 'types/entities/Reservation'
import {getCompanyDisplayName} from './companyUtils'
import {TableInitBase, TableUiState} from './hooks/useTable'
import {expandSearchFiltersWithMap, toSearchFilterList} from 'utils/utility'
import {CompanyBranchType} from 'types/enums/CompanyBranchType'
import {ReservationStatus} from 'types/enums/ReservationStatus'
import {buildTableRequest} from 'utils/table/query'
import {CompaniesContactFilterType} from 'types/enums/CompaniesContactFilterType'
import {CompaniesFilterType} from 'types/enums/CompaniesFilterType'

/**
 * Normalize row data - handles both single Company objects and nested { company: Company } objects
 */
export const normalize = (row: Company | { company: Company }): Company =>
    'company' in row ? row.company : row

/** Bookings count for Accounts table / company tabs — excludes deleted reservations. */
export const countCompanyBookings = (reservations?: Reservation[] | null): number =>
    (reservations ?? []).filter((reservation) => reservation.status !== ReservationStatus.Deleted).length

/**
 * Get first element from optional array
 */
export const getFirst = <T,>(arr?: T[]): T | undefined =>
    Array.isArray(arr) && arr.length > 0 ? arr[0] : undefined

/** True when an address has at least one location field filled (skips empty API stubs). */
export const isPopulatedCompanyAddress = (address?: CompanyAddress | null): boolean => {
    if (!address) {
        return false
    }

    return Boolean(
        address.street_name?.trim() ||
            address.postal_code?.trim() ||
            address.town?.trim() ||
            address.region?.trim()
    )
}

/** Prefer populated addresses for selects; fall back to full list when none are filled. */
export const getUsableCompanyAddresses = (
    addresses?: CompanyAddress[] | null
): CompanyAddress[] => {
    const list = addresses ?? []
    const populated = list.filter(isPopulatedCompanyAddress)
    return populated.length > 0 ? populated : list
}

const sortAddressesNewestFirst = (addresses: CompanyAddress[]): CompanyAddress[] =>
    [...addresses].sort((a, b) => {
        const updatedDiff =
            (Date.parse(b.updated_at || '') || 0) - (Date.parse(a.updated_at || '') || 0)
        if (updatedDiff !== 0) {
            return updatedDiff
        }
        return (b.id || 0) - (a.id || 0)
    })

/** Primary address row — matches backend `addresses` relation (town, region, street_name, …). */
export const getPrimaryCompanyAddress = (company: Company): CompanyAddress | undefined => {
    const addresses = company.addresses ?? []
    if (addresses.length === 0) {
        return undefined
    }

    if (company.default_address_id != null) {
        const defaultAddress = addresses.find((address) => address.id === company.default_address_id)
        // Honor an explicit default only when it actually has location data — many
        // companies ship empty stub rows (id 1…) ahead of real addresses.
        if (defaultAddress && isPopulatedCompanyAddress(defaultAddress)) {
            return defaultAddress
        }
    }

    const populated = addresses.filter(isPopulatedCompanyAddress)
    const pool = populated.length > 0 ? populated : addresses

    const companyNamed = pool.filter((address) => address.name === 'company')
    if (companyNamed.length > 0) {
        return sortAddressesNewestFirst(companyNamed)[0]
    }

    return sortAddressesNewestFirst(pool)[0]
}

/** Prefer the address list that still has real location data (skip empty stub dumps). */
export const preferRicherCompanyAddresses = (
    preferred?: CompanyAddress[] | null,
    fallback?: CompanyAddress[] | null
): CompanyAddress[] => {
    const primary = preferred ?? []
    const secondary = fallback ?? []
    const primaryPopulated = primary.filter(isPopulatedCompanyAddress).length
    const secondaryPopulated = secondary.filter(isPopulatedCompanyAddress).length

    if (primaryPopulated > 0 && primaryPopulated >= secondaryPopulated) {
        return primary
    }
    if (secondaryPopulated > 0) {
        return secondary
    }

    return primary.length > 0 ? primary : secondary
}

/** Region (Kanton) from the lead's company address, with optional event-form fallback. */
export const getLeadCompanyRegion = (lead: {
    company?: Company | null
    state?: string | null
}): string => {
    if (lead.company) {
        const region = getPrimaryCompanyAddress(lead.company)?.region?.trim()
        if (region) {
            return region
        }
    }

    return lead.state?.trim() ?? ''
}

export const getCompanyContactDisplayName = (contact: CompanyContact | undefined): string => {
    if (!contact) {
        return ''
    }

    const personName = `${contact.title ? contact.title + ' ' : ''}${contact.first_name || ''} ${contact.last_name || ''}`.trim()
    if (personName) {
        return personName
    }

    if (isGenericCompanyContact(contact)) {
        return formatGenericContactLocalLabel(contact.email) || contact.email || ''
    }

    return contact.email || ''
}

/** Primary contact row — matches backend `contacts` relation. Prefers a person over a generic mailbox. */
export const getPrimaryCompanyContact = (company: Company): CompanyContact | undefined => {
    return pickPreferredCompanyContact(company.contacts, company.default_contact_id)
}

export const hasCompanyContacts = (company: Company): boolean => {
    const contacts = company.contacts ?? []
    return contacts.length > 0 || Boolean(company.default_contact_id)
}

/**
 * Resolver for company name display
 */
export const nameResolver = (c: Company): string => getCompanyDisplayName(c)

/**
 * Resolver for company contact information (email + phone)
 */
export const contactResolver = (c: Company): string => {
    const contact = getPrimaryCompanyContact(c)
    return [getCompanyContactDisplayName(contact), contact?.email, contact?.phone].filter(Boolean).join(' ')
}

/**
 * Resolver for company location information (town, region, street_name, postal_code)
 */
export const locationResolver = (c: Company): string => {
    const address = getPrimaryCompanyAddress(c)
    if (!address) {
        return ''
    }
    return [address.town, address.region, address.street_name, address.postal_code]
        .filter(Boolean)
        .join(' ')
}

/**
 * Search field mapping for companies table - maps frontend search keys to API field names
 */
const ADDRESS_SEARCH_FIELDS = [
    'addresses.street_name',
    'addresses.region',
    'addresses.country',
    'addresses.postal_code',
    'addresses.town',
    'addresses.note',
] as const

/**
 * Maps UI column filter ids to API search field ids for POST /admin/companies/table.
 * Address-related columns search the nested addresses relation, not deprecated Company fields.
 */
/**
 * Maps UI column filter ids to API search field ids for POST /admin/companies/table.
 * Address-related columns search the nested addresses relation, not deprecated Company fields.
 */
export const SEARCH_MAP: Record<string, string[]> = {
    id: ['company.id'],
    seller_id: ['company.seller_id'],
    name: [
        'company.name',
        'company.first_name',
        'company.last_name',
        'contacts.first_name',
        'contacts.last_name',
    ],
    contact: [
        'contacts.first_name',
        'contacts.last_name',
        'contacts.phone',
        'contacts.note',
        // Prefer bare `email` (same as Contacts table) — `contacts.email` alone misses domains like h-och.
        'email',
        'contacts.email',
    ],
    lifecycle_status: ['lifecycle_status'],
    business_category: ['company.business_category'],
    employee_size: ['employee_size'],
    employee_count: ['company.employee_count'],
    /** Address column – search across all address fields */
    address: [...ADDRESS_SEARCH_FIELDS],
    location: [...ADDRESS_SEARCH_FIELDS],
    region: ['addresses.region'],
    postal_code: ['addresses.postal_code'],
    town: ['addresses.town'],
    street_name: ['addresses.street_name'],
    /** @deprecated Saved views / legacy column ids */
    state: ['addresses.region'],
    city: ['addresses.town'],
    street_address: ['addresses.street_name'],
    website: ['website'],
    origin: ['company.origin'],
    tags: ['tags'],
    contact_assignment: ['with_contacts'],
}

/** Mail-domain style tokens (e.g. h-och, @h-och.ch) — search email fields only for global `all`. */
const isMailDomainSearchToken = (token: string): boolean => {
    const value = token.trim().toLowerCase()
    if (!value) {
        return false
    }
    if (value.includes('@') || value.includes('.')) {
        return true
    }
    // Hyphenated brand/domain labels used as mail search (Task 84: h-och)
    return /^[a-z0-9]+(?:-[a-z0-9]+)+$/i.test(value)
}

const MAIL_SEARCH_FIELDS = ['email', 'contacts.email'] as const

/**
 * Maps UI column ids to API sort field ids for POST /admin/companies/table.
 * Address columns use the `addresses.*` relation prefix (same as SEARCH_MAP).
 */
export const SORT_MAP: Record<string, string> = {
    lifecycle_status: 'lifecycle_status',
    /** @deprecated Previous API field ids */
    account_status: 'lifecycle_status',
    lifecycle: 'lifecycle_status',
    region: 'addresses.region',
    postal_code: 'addresses.postal_code',
    town: 'addresses.town',
    street_name: 'addresses.street_name',
    address: 'addresses.town',
    /** @deprecated Saved views / legacy column ids */
    state: 'addresses.region',
    city: 'addresses.town',
    street_address: 'addresses.street_name',
    /** @deprecated Singular `address.*` from earlier SORT_MAP */
    'address.region': 'addresses.region',
    'address.town': 'addresses.town',
    'address.postal_code': 'addresses.postal_code',
    'address.street_name': 'addresses.street_name',
    'company.id': 'id',
    'company.name': 'name',
    'company.type': 'type',
}

const mapContactAssignmentSearchValue = (value: unknown): string | undefined => {
    if (value === CompaniesContactFilterType.HasContact) {
        return 'yes'
    }

    if (value === CompaniesContactFilterType.NoContact) {
        return 'no'
    }

    return undefined
}

/**
 * Split multi-word text filters into tokens so the API can AND them
 * (SEARCH_MAP fields for the same token stay OR'd).
 */
export const splitTextSearchTokens = (value: unknown): string[] => {
    if (typeof value !== 'string') {
        return []
    }
    return value.trim().split(/\s+/).filter(Boolean)
}

/**
 * Transform search filters from frontend format to API format using SEARCH_MAP
 */
export const transformCompaniesSearch = (filters: any[]): any[] => {
    const normalizedFilters = toSearchFilterList(filters).flatMap((filterItem) => {
        if (!filterItem?.id) {
            return []
        }

        if (filterItem.id === 'contact_assignment') {
            const mappedValue = mapContactAssignmentSearchValue(filterItem.value)
            return mappedValue ? [{ id: filterItem.id, value: mappedValue }] : []
        }

        if (typeof filterItem.value !== 'string') {
            return [filterItem]
        }

        const tokens = splitTextSearchTokens(filterItem.value)

        // Mail-domain tokens: email fields only. Mixing with name/phone makes the API AND and return 0.
        if (
            (filterItem.id === 'all' || filterItem.id === 'contact') &&
            tokens.length > 0 &&
            tokens.every(isMailDomainSearchToken)
        ) {
            return tokens.flatMap((token) =>
                MAIL_SEARCH_FIELDS.map((field) => ({ id: field, value: token }))
            )
        }

        return tokens.map((token) => ({
            id: filterItem.id,
            value: token,
        }))
    })

    return expandSearchFiltersWithMap(normalizedFilters, SEARCH_MAP)
}

/** Transform sorting from UI column ids to API field ids. */
export const transformCompaniesSorting = (
    sorting: { id: string; desc: boolean }[]
): { id: string; desc: boolean }[] =>
    sorting.map((sort) => ({
        ...sort,
        id: SORT_MAP[sort.id] ?? sort.id,
    }))

/** Tab type filter and the `type` column filter must not be sent together. */
export const resolveCompaniesTableInitForViewFilter = (
    tableInit: TableInitBase,
    filterType: CompaniesFilterType
): TableInitBase => {
    if (filterType === CompaniesFilterType.All) {
        return tableInit
    }

    return {
        ...tableInit,
        columnFilters: (tableInit.columnFilters ?? []).filter((filter) => filter.id !== 'type'),
    }
}

/** Keep the Account type column filter aligned with the active Accounts tab. */
export const getCompaniesTypeFilterForView = (
    filterType: CompaniesFilterType
): CompanyBranchType[] => {
    if (filterType === CompaniesFilterType.All) {
        return [
            CompanyBranchType.Parent,
            CompanyBranchType.Branch,
            CompanyBranchType.Company,
            CompanyBranchType.School,
            CompanyBranchType.Verein,
        ]
    }

    const typeByView: Record<Exclude<CompaniesFilterType, CompaniesFilterType.All>, CompanyBranchType> = {
        [CompaniesFilterType.Parents]: CompanyBranchType.Parent,
        [CompaniesFilterType.Branches]: CompanyBranchType.Branch,
        [CompaniesFilterType.Private]: CompanyBranchType.Private,
        [CompaniesFilterType.Company]: CompanyBranchType.Company,
        [CompaniesFilterType.School]: CompanyBranchType.School,
        [CompaniesFilterType.Verein]: CompanyBranchType.Verein,
    }

    return [typeByView[filterType]]
}

export const buildCompaniesTableRequestParams = (
    tableInit: TableInitBase,
    filterType: CompaniesFilterType,
    options?: { includeColumnVisibility?: boolean }
) => {
    const tableInitForRequest = resolveCompaniesTableInitForViewFilter(tableInit, filterType)

    return buildTableRequest({
        tableInit: tableInitForRequest,
        extras: {
            type: filterType,
        },
        includeGlobalAsAll: true,
        includeColumnVisibility: options?.includeColumnVisibility ?? false,
        splitSorting: true,
        encodeSearchAsJsonString: false,
        transformSearch: transformCompaniesSearch,
        transformSorting: transformCompaniesSorting,
    })
}

/** Renamed or removed companies table column ids (saved views / redux may still use legacy keys). */
const COMPANIES_COLUMN_ID_MIGRATION: Record<string, string> = {
    state: 'region',
    city: 'town',
    street_address: 'street_name',
    email: 'contact',
    phone: 'contact',
}

const REMOVED_COMPANIES_COLUMN_IDS = new Set(['email', 'phone'])

const migrateColumnId = (id: string): string => COMPANIES_COLUMN_ID_MIGRATION[id] ?? id

/** Migrate persisted companies table UI (column visibility, filters, sorting) to current column ids. */
export const migrateCompaniesTableUi = (ui: TableUiState): TableUiState => {
    const columnVisibility = Object.fromEntries(
        Object.entries(ui.columnVisibility ?? {})
            .filter(([key]) => !REMOVED_COMPANIES_COLUMN_IDS.has(migrateColumnId(key)))
            .map(([key, visible]) => [migrateColumnId(key), visible])
    )
    const columnFilters = (ui.columnFilters ?? []).flatMap((filter) => {
        const id = migrateColumnId(filter.id)
        if (REMOVED_COMPANIES_COLUMN_IDS.has(filter.id)) {
            return [{ ...filter, id: 'contact' }]
        }
        return [{ ...filter, id }]
    })
    const sorting = (ui.sorting ?? [])
        .filter((sort) => !REMOVED_COMPANIES_COLUMN_IDS.has(sort.id))
        .map((sort) => ({
            ...sort,
            id: migrateColumnId(sort.id),
        }))
    return { ...ui, columnVisibility, columnFilters, sorting }
}

/**
 * Default table UI state for companies
 */
export const DEFAULT_COMPANIES_TABLE_UI: TableUiState = {
    pageIndex: 0,
    pageSize: 50,
    globalFilter: '',
    columnFilters: [
        {
            id: 'type',
            value: [
                CompanyBranchType.Parent,
                CompanyBranchType.Branch,
                CompanyBranchType.Company,
                CompanyBranchType.School,
                CompanyBranchType.Verein,
            ],
        },
    ],
    sorting: [{ id: 'id', desc: true }],
    columnVisibility: {
        contact_assignment: false,
        business_category: false,
        employee_size: false,
        employee_count: false,
        region: false,
        postal_code: false,
        town: false,
        street_name: false,
        website: false,
    },
}

/** Reset shows every column; only filters, sort and pagination match the default. */
export const COMPANIES_TABLE_RESET_UI: TableUiState = {
    ...DEFAULT_COMPANIES_TABLE_UI,
    columnVisibility: {},
}
